/**
 * ============================================================================
 * ClinIA Perú - Plantillas de Receta Médica
 * ============================================================================
 * Módulo único que genera el HTML imprimible de la receta. Antes esta lógica
 * estaba duplicada en ConsultationsPage y PatientMedicalHistory, con formatos
 * distintos entre sí; ahora ambas páginas llaman a `generatePrescriptionHTML`.
 *
 * Plantillas disponibles:
 *
 *   v1 - "Clásica vertical". Dos hojas A4 verticales: una de Tratamiento
 *        (diagnóstico, procedimientos, exámenes, interconsultas) y otra de
 *        Receta con solo los medicamentos, para que el paciente pueda
 *        entregarla en farmacia sin exponer su diagnóstico.
 *
 *   v2 - "Recetario horizontal". Una sola hoja A4 apaisada partida por una
 *        línea de corte: la mitad izquierda lleva los datos del paciente, los
 *        diagnósticos con CIE-10 y la tabla de medicamentos; la mitad derecha
 *        lleva una franja con la identificación del paciente, cómo tomar cada
 *        medicamento (dosis, frecuencia, vía, duración e indicaciones), las
 *        indicaciones adicionales (nutrición, exámenes, interconsultas) y la
 *        próxima cita.
 *
 *        Las dos mitades se cortan y viajan por separado, así que cada una
 *        identifica al paciente por su cuenta.
 *
 * El médico elige la plantilla y los bloques opcionales en Configuración; esos
 * valores llegan aquí dentro del objeto `branding`.
 * ============================================================================
 */

/**
 * Toggles de bloques opcionales. Se usan cuando el médico todavía no ha
 * guardado su configuración, y como base para completar la que ya guardó.
 */
export const DEFAULT_PRESCRIPTION_OPTIONS = {
  show_cie10: true,             // Cajas de código CIE-10 junto a cada diagnóstico
  show_next_appointment: true,  // Línea "Próxima cita: __/__/__"
  show_contact_footer: true,    // Pie con teléfono y dirección del consultorio
  show_digital_stamp: true,     // Leyenda "Documento generado digitalmente..."
  show_cut_line: true,          // Línea punteada de corte entre las dos mitades (v2)
  show_exams: true,             // Laboratorio, imágenes e interconsultas en la receta
};

/** Filas de la tabla si el médico no configuró un mínimo */
export const DEFAULT_MIN_ROWS = 8;

/**
 * Escapa texto antes de interpolarlo en el HTML.
 * Los datos vienen de formularios libres y de transcripciones de IA, así que
 * un `<` suelto no debe romper el documento impreso.
 *
 * @param {*} value - Valor a escapar
 * @returns {string} Texto seguro para interpolar
 */
const esc = (value) => {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
};

/** Escapa un texto y convierte los saltos de línea en <br> */
const escMultiline = (value) => esc(value).replace(/\n/g, '<br>');

/**
 * Parsea el plan de tratamiento de JSON string a objeto.
 * Las consultas antiguas guardaban texto plano; en ese caso se devuelve
 * dentro de la propiedad `texto` para no perder la información.
 *
 * @param {string|Object} treatmentPlan - JSON string (o ya objeto) del plan
 * @returns {Object|null} Plan parseado o null si no hay nada
 */
export const parseTreatmentPlan = (treatmentPlan) => {
  if (!treatmentPlan) return null;
  if (typeof treatmentPlan === 'object') return treatmentPlan;
  try {
    return JSON.parse(treatmentPlan);
  } catch {
    return { texto: treatmentPlan };
  }
};

/**
 * Devuelve los diagnósticos como lista normalizada `[{diagnosis, cie10_code}]`.
 *
 * Fuente preferida: `treatment_plan.diagnoses`, que es donde el formulario
 * guarda cada diagnóstico con su código CIE-10 por separado. Si la consulta es
 * anterior a eso, se reconstruye desde el string `consultation.diagnosis`, que
 * tiene el formato "Diagnóstico (CIE10) | Otro diagnóstico (CIE10)".
 *
 * @param {Object} plan - Plan de tratamiento parseado
 * @param {Object} consultation - Consulta
 * @returns {Array<{diagnosis: string, cie10_code: string}>}
 */
export const getDiagnoses = (plan, consultation) => {
  const structured = plan?.diagnoses;
  if (Array.isArray(structured) && structured.length > 0) {
    const normalized = structured
      .filter(d => d && (d.diagnosis || '').trim())
      .map(d => ({ diagnosis: d.diagnosis.trim(), cie10_code: (d.cie10_code || '').trim() }));
    if (normalized.length > 0) return normalized;
  }

  return parseDiagnosisString(consultation?.diagnosis);
};

/**
 * Reconstruye la lista de diagnósticos desde el string plano que guarda la
 * consulta, con formato "Diagnóstico (CIE10) | Otro diagnóstico (CIE10)".
 *
 * Se usa para consultas antiguas (sin diagnósticos estructurados) y al editar
 * una consulta, donde el formulario trabaja con el string.
 *
 * @param {string} raw - String de diagnósticos
 * @returns {Array<{diagnosis: string, cie10_code: string, is_primary: boolean}>}
 */
export const parseDiagnosisString = (raw) => {
  const text = (raw || '').trim();
  if (!text) return [];

  return text.split('|').map(part => {
    const chunk = part.trim();
    const match = chunk.match(/\(([^)]+)\)\s*$/);
    return match
      ? { diagnosis: chunk.replace(/\s*\([^)]+\)\s*$/, '').trim(), cie10_code: match[1].trim() }
      : { diagnosis: chunk, cie10_code: '' };
  })
    .filter(d => d.diagnosis)
    // El primero es el diagnóstico principal: así se guarda al crear la consulta
    .map((d, idx) => ({ ...d, is_primary: idx === 0 }));
};

/**
 * Normaliza los datos del médico a partir del branding guardado.
 * Centraliza los valores por defecto para que las dos plantillas muestren
 * exactamente lo mismo.
 */
const buildDoctorInfo = (branding = {}) => ({
  name: branding.full_name || 'Médico Tratante',
  specialization: branding.specialization || '',
  license: branding.license_number || '',
  rne: branding.rne_number || '',
  email: branding.email_professional || branding.email || '',
  phone: branding.phone_professional || branding.phone || '',
  address: branding.address_professional || '',
  businessName: branding.business_name || '',
  logoRect: branding.logo_rectangular || '',
  logoSquare: branding.logo_square || '',
  primaryColor: branding.primary_color || '#2563eb',
  secondaryColor: branding.secondary_color || '#1e40af',
});

/**
 * Normaliza los datos del paciente. La edad se calcula a partir de la fecha de
 * nacimiento teniendo en cuenta si ya cumplió años este año.
 */
const buildPatientInfo = (patient = {}) => {
  let age = null;
  if (patient?.birth_date) {
    const birth = new Date(patient.birth_date);
    if (!isNaN(birth)) {
      const today = new Date();
      age = today.getFullYear() - birth.getFullYear();
      const monthDiff = today.getMonth() - birth.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) age -= 1;
    }
  }

  return {
    name: `${patient?.first_name || ''} ${patient?.last_name || ''}`.trim(),
    dni: patient?.dni || '',
    age,
    historyNumber: patient?.medical_record_number || patient?.history_number || '',
  };
};

/** Fecha larga en español: "5 de marzo de 2026" */
const formatLongDate = (value) => {
  const date = value ? new Date(value) : new Date();
  if (isNaN(date)) return '';
  return date.toLocaleDateString('es-PE', { day: 'numeric', month: 'long', year: 'numeric' });
};

/** Fecha corta: "05/03/2026" */
const formatShortDate = (value) => {
  const date = value ? new Date(value) : new Date();
  if (isNaN(date)) return '';
  return date.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

/**
 * Une las opciones guardadas por el médico con los valores por defecto.
 * Una opción ausente se considera activada, no desactivada.
 */
const resolveOptions = (branding = {}) => ({
  ...DEFAULT_PRESCRIPTION_OPTIONS,
  ...(branding.prescription_options || {}),
});

/**
 * Número de filas a dibujar en las tablas de la receta v2.
 * Nunca se recorta la lista de medicamentos: si hay más medicamentos que el
 * mínimo configurado, se dibujan todos.
 */
const resolveRowCount = (branding = {}, medicationCount = 0) => {
  const configured = Number(branding.prescription_min_rows);
  const minRows = Number.isFinite(configured) && configured > 0 ? configured : DEFAULT_MIN_ROWS;
  return Math.max(minRows, medicationCount);
};

// ============================================================================
// PLANTILLA V1 - Clásica vertical (dos hojas A4)
// ============================================================================

/**
 * Genera el documento clásico: hoja de Tratamiento + hoja de Receta.
 *
 * @param {Object} ctx - Contexto ya normalizado
 * @returns {string} HTML completo
 */
const renderTemplateV1 = ({ consultation, plan, doctor, patient, options }) => {
  const pc = doctor.primaryColor;
  const medications = plan?.medicamentos || [];
  const hasMedications = medications.length > 0;
  const consultationDate = formatLongDate(consultation?.consultation_date);

  const logoBlock = doctor.logoRect
    ? `<img src="${esc(doctor.logoRect)}" alt="Logo" />`
    : doctor.businessName
      ? `<h1>${esc(doctor.businessName)}</h1>`
      : `<div class="placeholder-logo">Logo de empresa</div>`;

  /** Cabecera con logo y el título de la hoja */
  const header = (sheetTitle) => `
    <div class="header">
      ${logoBlock}
      <p class="sheet-title">${esc(sheetTitle)}</p>
    </div>`;

  /** Datos de paciente, médico y fecha (se repiten en ambas hojas) */
  const infoSection = `
    <div class="info-section">
      <div class="info-block">
        <h3>Paciente</h3>
        <p><strong>${esc(patient.name) || 'N/A'}</strong></p>
        <p>DNI: ${esc(patient.dni) || 'N/A'}</p>
        <p>Edad: ${patient.age !== null ? `${patient.age} años` : 'N/A'}</p>
      </div>
      <div class="info-block">
        <h3>Médico</h3>
        <p><strong>Dr(a). ${esc(doctor.name)}</strong></p>
        <p>${esc(doctor.specialization)}</p>
        <p>${esc(doctor.license)}</p>
      </div>
      <div class="info-block">
        <h3>Fecha</h3>
        <p>${esc(consultationDate)}</p>
      </div>
    </div>`;

  /** Firma del médico al pie de cada hoja */
  const footer = `
    <div class="footer">
      <div class="signature-line"></div>
      <div class="footer-content">
        ${doctor.logoSquare ? `<div class="footer-logo"><img src="${esc(doctor.logoSquare)}" alt="Logo" /></div>` : ''}
        <div class="footer-info">
          <div class="name">Dr(a). ${esc(doctor.name)}</div>
          <div class="detail">${esc(doctor.specialization)}</div>
          <div class="detail">${esc(doctor.license)}</div>
          ${doctor.email ? `<div class="detail">${esc(doctor.email)}</div>` : ''}
        </div>
      </div>
      ${options.show_contact_footer && (doctor.phone || doctor.address) ? `
      <p class="contact">${[doctor.phone, doctor.address].filter(Boolean).map(esc).join(' &nbsp;·&nbsp; ')}</p>
      ` : ''}
      ${options.show_digital_stamp ? `
      <p class="stamp">Documento generado digitalmente por ClinIA Perú - ${esc(new Date().toLocaleString('es-PE'))}</p>
      ` : ''}
    </div>`;

  /** Bloque de lista con motivo opcional (laboratorio, imágenes, etc.) */
  const listSection = (title, items, motivo) => (items && items.length > 0) ? `
    <div class="section">
      <h2>${esc(title)}</h2>
      <div class="section-body">
        <ul>${items.map(item => `<li>${esc(item)}</li>`).join('')}</ul>
        ${motivo ? `<p class="motivo"><strong>Motivo de la solicitud:</strong> ${escMultiline(motivo)}</p>` : ''}
      </div>
    </div>` : '';

  return `
    <!DOCTYPE html>
    <html lang="es">
    <head>
      <meta charset="UTF-8">
      <title>Tratamiento y Receta - ${esc(patient.name)}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Segoe UI', Arial, sans-serif; background: #fff; color: #333; }

        /* Cada .sheet es una hoja física independiente */
        .sheet { max-width: 800px; margin: 0 auto; padding: 32px 40px; }
        .sheet + .sheet { page-break-before: always; border-top: 1px dashed #d1d5db; }

        .header { text-align: center; border-bottom: 3px solid ${pc}; padding-bottom: 14px; margin-bottom: 20px; }
        .header img { max-height: 70px; max-width: 320px; object-fit: contain; margin-bottom: 8px; }
        .header h1 { color: ${pc}; font-size: 24px; margin-bottom: 4px; }
        .header .sheet-title { color: ${pc}; font-size: 20px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; }
        .placeholder-logo { border: 2px dashed #ccc; padding: 12px 26px; display: inline-block; color: #999; font-size: 13px; border-radius: 8px; margin-bottom: 8px; }

        .info-section { display: flex; justify-content: space-between; gap: 16px; margin-bottom: 18px; padding: 14px 18px; background: #f8fafc; border-radius: 8px; }
        .info-block h3 { color: ${pc}; font-size: 11px; text-transform: uppercase; margin-bottom: 4px; }
        .info-block p { font-size: 13px; }

        .diagnosis-section { padding: 14px 18px; border-radius: 8px; margin-bottom: 18px; background: #f8fafc; border-left: 4px solid ${pc}; }
        .diagnosis-section h2 { color: ${pc}; font-size: 15px; margin-bottom: 6px; }
        .diagnosis-section p { font-size: 14px; }
        .diagnosis-section .cie { color: #666; font-size: 12px; }

        .section { margin-bottom: 18px; page-break-inside: avoid; }
        .section h2 { color: ${pc}; font-size: 16px; margin-bottom: 10px; padding-bottom: 6px; border-bottom: 2px solid #e5e7eb; }
        .section-body { padding: 12px 16px; background: #f8fafc; border-radius: 8px; border-left: 4px solid ${pc}; font-size: 14px; }
        .section-body ul { margin: 0; padding-left: 18px; }
        .section-body li { margin-bottom: 4px; }
        .motivo { margin-top: 8px; padding-top: 8px; border-top: 1px dashed #cbd5e1; font-size: 13px; }
        .motivo strong { color: ${pc}; }

        .medication { border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px 14px; margin-bottom: 10px; page-break-inside: avoid; }
        .medication-name { font-weight: bold; font-size: 15px; color: ${pc}; }
        .medication-details { display: grid; grid-template-columns: repeat(5, 1fr); gap: 8px; margin-top: 8px; }
        .medication-detail { background: #f8fafc; padding: 6px 8px; border-radius: 4px; }
        .medication-detail label { font-size: 9px; color: #666; text-transform: uppercase; display: block; }
        .medication-detail span { font-size: 12px; font-weight: 500; }
        .medication-detail.full { grid-column: 1 / -1; }

        .footer { margin-top: 28px; padding-top: 18px; border-top: 2px solid #e5e7eb; page-break-inside: avoid; }
        .signature-line { width: 200px; border-top: 1px solid #333; margin: 26px auto 10px; }
        .footer-content { display: flex; align-items: center; justify-content: center; gap: 20px; }
        .footer-logo img { width: 56px; height: 56px; object-fit: contain; border-radius: 8px; }
        .footer-info { text-align: center; }
        .footer-info .name { font-size: 15px; font-weight: bold; }
        .footer-info .detail { font-size: 12px; color: #666; margin-top: 2px; }
        .contact { margin-top: 10px; font-size: 11px; color: #555; text-align: center; }
        .stamp { margin-top: 10px; font-size: 10px; color: #999; text-align: center; }

        @page { size: A4; margin: 12mm; }
        @media print {
          .sheet { padding: 0; max-width: none; }
          .sheet + .sheet { border-top: none; }
          .no-print { display: none; }
        }
      </style>
    </head>
    <body>

      <!-- ============ HOJA 1: TRATAMIENTO ============ -->
      <div class="sheet">
        ${header('Tratamiento')}
        ${infoSection}

        <div class="diagnosis-section">
          <h2>Diagnóstico</h2>
          ${consultation.diagnoses.length > 0
            ? consultation.diagnoses.map((d, i) => `
              <p>${consultation.diagnoses.length > 1 ? `${i + 1}. ` : ''}${esc(d.diagnosis)}${
                d.cie10_code && options.show_cie10 ? ` <span class="cie">(CIE-10: ${esc(d.cie10_code)})</span>` : ''
              }</p>`).join('')
            : '<p>No especificado</p>'}
        </div>

        ${listSection('Procedimientos Realizados', plan?.procedimientos, plan?.procedimientos_motivo)}

        ${plan?.nutricion ? `
        <div class="section">
          <h2>Indicaciones Nutricionales</h2>
          <div class="section-body">${escMultiline(plan.nutricion)}</div>
        </div>
        ` : ''}

        ${options.show_exams ? `
          ${listSection('Exámenes de Laboratorio Solicitados', plan?.laboratorio, plan?.laboratorio_motivo)}
          ${listSection('Estudios de Imagen Solicitados', plan?.imagenes, plan?.imagenes_motivo)}
          ${listSection('Interconsultas Solicitadas', plan?.interconsultas)}
        ` : ''}

        ${consultation.indications ? `
        <div class="section">
          <h2>Indicaciones Adicionales</h2>
          <div class="section-body">${escMultiline(consultation.indications)}</div>
        </div>
        ` : ''}

        ${hasMedications ? `
        <div class="section">
          <div class="section-body">
            Los medicamentos indicados se detallan en la <strong>hoja de Receta</strong> adjunta.
          </div>
        </div>
        ` : ''}

        ${footer}
      </div>

      <!-- ============ HOJA 2: RECETA (para el farmacéutico) ============ -->
      ${hasMedications ? `
      <div class="sheet">
        ${header('Receta')}
        ${infoSection}

        <div class="section">
          ${medications.map((med, idx) => `
            <div class="medication">
              <div class="medication-name">${idx + 1}. ${esc(med.nombre)}${med.concentracion ? ` ${esc(med.concentracion)}` : ''}${med.forma_farmaceutica ? ` - ${esc(med.forma_farmaceutica)}` : ''}</div>
              <div class="medication-details">
                <div class="medication-detail"><label>Cantidad</label><span>${esc(med.cantidad) || '-'}</span></div>
                <div class="medication-detail"><label>Dosis</label><span>${esc(med.dosis) || '-'}</span></div>
                <div class="medication-detail"><label>Frecuencia</label><span>${esc(med.frecuencia) || '-'}</span></div>
                <div class="medication-detail"><label>Vía</label><span>${esc(med.via) || '-'}</span></div>
                <div class="medication-detail"><label>Duración</label><span>${esc(med.duracion) || '-'}</span></div>
                ${med.indicaciones ? `
                <div class="medication-detail full"><label>Indicaciones</label><span>${escMultiline(med.indicaciones)}</span></div>
                ` : ''}
              </div>
            </div>
          `).join('')}
        </div>

        ${footer}
      </div>
      ` : ''}

    </body>
    </html>
  `;
};

// ============================================================================
// PLANTILLA V2 - Recetario horizontal (una hoja A4 apaisada, dos mitades)
// ============================================================================

/**
 * Genera el recetario horizontal.
 *
 * La hoja se divide en dos mitades separadas por una línea de corte:
 *   Izquierda -> datos del paciente, diagnósticos con CIE-10 y tabla
 *                "Tratamiento – Receta" (medicamento, concentración, forma
 *                farmacéutica, cantidad). Es la mitad que va a farmacia.
 *   Derecha   -> franja de identificación, tabla "Indicaciones" (medicamento
 *                con su concentración, dosis, frecuencia, vía, duración y las
 *                indicaciones de administración), indicaciones adicionales y
 *                próxima cita. Es la mitad que se queda el paciente.
 *
 * Ambas mitades repiten cabecera, firma y pie de contacto para que funcionen
 * como documentos independientes una vez cortadas.
 *
 * @param {Object} ctx - Contexto ya normalizado
 * @returns {string} HTML completo
 */
/**
 * Tamaño de letra de una celda según cuánto texto tiene que caber en ella.
 *
 * El problema que resuelve: una indicación larga en una columna estrecha
 * ("APLICAR UNA PEQUEÑA PORCION DE LA CREMA DENTRO DEL CANAL ANAL DESPUES DEL
 * BAÑO..." dentro de la columna Dosis) se apila en diez líneas y estira toda la
 * fila, empujando el resto de la receta y dejando la hoja desequilibrada.
 *
 * En lugar de recortar el texto —en una receta no se puede perder ni una
 * palabra— se encoge la letra solo en esa celda. Las demás mantienen su tamaño.
 *
 * La decisión se toma sobre la DENSIDAD, no sobre la longitud: 40 caracteres en
 * una columna del 25% caben cómodos y en una del 10% no. Densidad es
 * caracteres por punto porcentual de ancho.
 *
 * @param {string} value - Texto de la celda
 * @param {string} width - Ancho de la columna tal como se declara ("13%")
 * @returns {number} Tamaño en puntos, entre 5.6 y 8
 */
const fitFontSize = (value, width) => {
  const text = String(value || '');
  const widthPct = parseFloat(width) || 100;
  if (!text) return 8;

  const density = text.length / widthPct;

  if (density <= 1.6) return 8;      // cabe holgado: tamaño normal
  if (density <= 2.6) return 7;
  if (density <= 4) return 6.2;
  return 5.6;                        // por debajo deja de ser legible impreso
};

const renderTemplateV2 = ({ consultation, plan, doctor, patient, options, rowCount }) => {
  const pc = doctor.primaryColor;
  const medications = plan?.medicamentos || [];
  const consultationDate = formatShortDate(consultation?.consultation_date);

  // Altura de fila calculada para que la receta quepa en una sola hoja.
  // Con el mínimo habitual (8 filas) se usa la altura cómoda de 9mm; si el
  // médico receta más medicamentos, las filas se comprimen en lugar de empujar
  // la firma a una segunda hoja casi vacía. Por debajo de 5mm dejan de ser
  // legibles, así que ahí sí se permite que el documento crezca.
  const ROWS_AVAILABLE_MM = 67;
  const rowHeight = Math.max(5, Math.min(9, ROWS_AVAILABLE_MM / Math.max(rowCount, 1)));

  /** Cabecera: logo a la izquierda, datos del médico a la derecha */
  const header = `
    <div class="rx-header">
      <div class="rx-brand">
        ${doctor.logoRect
          ? `<img src="${esc(doctor.logoRect)}" alt="Logo" />`
          : doctor.businessName
            ? `<span class="rx-brand-name">${esc(doctor.businessName)}</span>`
            : `<span class="rx-brand-placeholder">Logo</span>`}
      </div>
      <div class="rx-doctor">
        <div class="rx-doctor-name">Dr(a). ${esc(doctor.name)}</div>
        <div class="rx-doctor-license">
          ${[doctor.license, doctor.rne ? `RNE ${doctor.rne}` : '', doctor.specialization]
            .filter(Boolean).map(esc).join(' &nbsp; ')}
        </div>
      </div>
    </div>`;

  /** Caja con etiqueta arriba y valor debajo, como el recetario preimpreso */
  const field = (label, value, extraClass = '') => `
    <div class="rx-field ${extraClass}">
      <span class="rx-field-label">${esc(label)}</span>
      <span class="rx-field-value">${esc(value)}</span>
    </div>`;

  /** Bloque de diagnósticos: siempre tres filas, se completen o no */
  const diagnosisRows = Array.from({ length: 3 }, (_, idx) => {
    const dx = consultation.diagnoses[idx];
    return `
      <div class="rx-dx-row">
        ${field(`Diagnóstico Dx ${idx + 1}:`, dx?.diagnosis || '', 'rx-dx-name')}
        ${options.show_cie10 ? field('CIE-10:', dx?.cie10_code || '', 'rx-dx-code') : ''}
      </div>`;
  }).join('');

  /**
   * Tabla de la receta. `columns` define encabezado y cómo se extrae el valor
   * de cada medicamento. Las filas sin medicamento quedan vacías para escribir
   * a mano, que es el punto de este formato.
   */
  const table = (columns) => `
    <table class="rx-table">
      <thead>
        <tr>
          <th class="rx-col-num">N°</th>
          ${columns.map(col => `<th style="width:${col.width}">${col.title}</th>`).join('')}
        </tr>
      </thead>
      <tbody>
        ${Array.from({ length: rowCount }, (_, idx) => {
          const med = medications[idx];
          return `
            <tr>
              <td class="rx-col-num">${idx + 1}</td>
              ${columns.map(col => {
                const value = med ? col.value(med) : '';
                return `<td style="font-size:${fitFontSize(value, col.width)}pt">${esc(value)}</td>`;
              }).join('')}
            </tr>`;
        }).join('')}
      </tbody>
    </table>`;

  /** Firma con línea sobre la leyenda "Firma y sello" */
  const signature = `
    <div class="rx-signature">
      <div class="rx-signature-line"></div>
      <span>Firma y sello</span>
    </div>`;

  /**
   * Franja compacta de identificación para la mitad del paciente.
   *
   * La mitad izquierda lleva la ficha completa del paciente; esta mitad no
   * llevaba ninguna. Una vez cortada la hoja, el paciente se quedaba con un
   * papel sin nombre, sin documento y sin fecha: imposible de archivar en su
   * historia y sin valor si tiene que mostrarlo en otro sitio.
   *
   * Va en una sola línea para no comerle espacio a la tabla de indicaciones.
   */
  const patientStrip = `
    <div class="rx-strip">
      <span><strong>${esc(patient.name)}</strong></span>
      ${patient.dni ? `<span>DNI ${esc(patient.dni)}</span>` : ''}
      ${patient.age !== null ? `<span>${esc(patient.age)} años</span>` : ''}
      <span class="rx-strip-date">${esc(consultationDate)}</span>
    </div>`;

  /** Pie con teléfono y dirección del consultorio */
  const contactFooter = options.show_contact_footer && (doctor.phone || doctor.address) ? `
    <div class="rx-contact">
      ${doctor.phone ? `<span>${esc(doctor.phone)}</span>` : ''}
      ${doctor.address ? `<span>${esc(doctor.address)}</span>` : ''}
    </div>` : '';

  /**
   * Contenido de "Indicaciones adicionales".
   * Aquí es donde aterriza todo lo que en la plantilla v1 vivía en la hoja de
   * Tratamiento: nutrición, exámenes, imágenes, interconsultas y
   * procedimientos. Así el formato horizontal no pierde información clínica.
   */
  const additionalIndications = () => {
    const blocks = [];

    if (consultation.indications) blocks.push(escMultiline(consultation.indications));

    // Consultas anteriores al plan estructurado guardaban todo el tratamiento
    // como texto libre; `parseTreatmentPlan` lo deja en `texto`. Sin esto, al
    // reimprimir una receta antigua salía vacía.
    if (plan?.texto) blocks.push(escMultiline(plan.texto));
    if (plan?.nutricion) blocks.push(`<strong>Nutrición:</strong> ${escMultiline(plan.nutricion)}`);

    if (options.show_exams) {
      const compact = (label, items, motivo) => {
        if (!items || items.length === 0) return null;
        const motivoText = motivo ? ` <em>(${esc(motivo)})</em>` : '';
        return `<strong>${label}:</strong> ${items.map(esc).join(', ')}${motivoText}`;
      };
      [
        compact('Laboratorio', plan?.laboratorio, plan?.laboratorio_motivo),
        compact('Imágenes', plan?.imagenes, plan?.imagenes_motivo),
        compact('Interconsultas', plan?.interconsultas),
        compact('Procedimientos', plan?.procedimientos, plan?.procedimientos_motivo),
      ].filter(Boolean).forEach(block => blocks.push(block));
    }

    return blocks.map(block => `<p>${block}</p>`).join('');
  };

  return `
    <!DOCTYPE html>
    <html lang="es">
    <head>
      <meta charset="UTF-8">
      <title>Receta Médica - ${esc(patient.name)}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Segoe UI', Arial, sans-serif; background: #eef1f5; color: #1f2933; }

        /* Hoja A4 apaisada. min-height (no height) para que la hoja crezca
           si el médico configuró muchas filas, en vez de recortar contenido. */
        .rx-sheet {
          width: 297mm;
          min-height: 210mm;
          margin: 0 auto;
          background: #fff;
          display: flex;
          align-items: stretch;
          padding: 8mm;
          gap: 6mm;
        }

        /* Cada mitad es un documento completo por sí mismo */
        .rx-half {
          flex: 1 1 0;
          display: flex;
          flex-direction: column;
          min-width: 0;
        }

        /* Línea de corte entre las dos mitades */
        .rx-cut { width: 0; border-left: 1px dashed #94a3b8; }

        /* --- Cabecera --- */
        .rx-header { display: flex; align-items: center; gap: 10px; padding-bottom: 6px; margin-bottom: 8px; }
        .rx-brand { flex: 0 0 auto; max-width: 42%; }
        .rx-brand img { max-height: 20mm; max-width: 100%; object-fit: contain; display: block; }
        .rx-brand-name { font-size: 13pt; font-weight: 700; color: ${pc}; }
        .rx-brand-placeholder { display: inline-block; border: 1px dashed #cbd5e1; padding: 8px 16px; color: #94a3b8; font-size: 8pt; border-radius: 4px; }
        .rx-doctor { flex: 1 1 auto; border-left: 1px solid #cbd5e1; padding-left: 10px; }
        .rx-doctor-name { font-size: 14pt; font-weight: 400; color: #1f2933; line-height: 1.2; }
        .rx-doctor-license { font-size: 8.5pt; color: #52606d; margin-top: 2px; }

        /* --- Campos tipo formulario --- */
        .rx-fields { display: flex; flex-direction: column; gap: 4px; margin-bottom: 6px; }
        .rx-field-row { display: flex; gap: 4px; }
        .rx-field {
          border: 1px solid #9aa5b1;
          border-radius: 3px;
          padding: 3px 5px;
          min-height: 9mm;
          flex: 1 1 0;
          min-width: 0;
        }
        .rx-field-label { display: block; font-size: 7pt; color: #52606d; line-height: 1.1; }
        .rx-field-value { display: block; font-size: 9pt; font-weight: 600; color: #1f2933; line-height: 1.2; word-break: break-word; }

        /* Franja de identificación de la mitad del paciente */
        .rx-strip {
          display: flex;
          flex-wrap: wrap;
          align-items: baseline;
          gap: 4px 10px;
          border: 1px solid #9aa5b1;
          border-radius: 3px;
          padding: 3px 6px;
          font-size: 8.5pt;
          margin-bottom: 4px;
        }
        .rx-strip strong { font-weight: 700; }
        .rx-strip-date { margin-left: auto; color: #52606d; }

        .rx-dx-row { display: flex; gap: 4px; margin-bottom: 4px; }
        .rx-dx-name { flex: 3 1 0; }
        .rx-dx-code { flex: 1 1 0; }

        /* --- Títulos de sección --- */
        .rx-section-title { font-size: 11pt; font-weight: 700; color: #1f2933; margin: 8px 0 6px; }
        .rx-section-title.center { text-align: center; }

        /* --- Tablas --- */
        .rx-table { width: 100%; border-collapse: collapse; table-layout: fixed; }
        .rx-table th, .rx-table td {
          border: 1px solid #9aa5b1;
          padding: 3px 4px;
          /* Tamaño por defecto; cada celda puede reducirlo con un style
             propio cuando su contenido es largo (ver fitFontSize). */
          font-size: 8pt;
          vertical-align: top;
          overflow-wrap: break-word;
        }
        .rx-table th { font-weight: 400; color: #52606d; font-size: 7.5pt; text-align: left; line-height: 1.15; }
        .rx-table td { height: ${rowHeight.toFixed(2)}mm; }
        .rx-col-num { width: 8mm; text-align: center; font-size: 11pt; color: #52606d; }
        .rx-table tbody .rx-col-num { font-weight: 300; }

        /* --- Indicaciones adicionales --- */
        .rx-notes {
          border: 1px solid #9aa5b1;
          border-radius: 3px;
          padding: 4px 6px;
          margin-top: 8px;
          flex: 1 1 auto;
          min-height: 28mm;
          min-width: 0;
          overflow-wrap: anywhere;
        }
        .rx-notes-label { font-size: 7pt; color: #52606d; }
        .rx-notes-body { font-size: 8.5pt; line-height: 1.35; margin-top: 2px; overflow-wrap: anywhere; }
        .rx-notes-body p { margin-bottom: 3px; }

        /* --- Pie: próxima cita, firma, contacto --- */
        .rx-bottom { margin-top: auto; padding-top: 8px; }
        .rx-appointment { font-size: 9pt; margin-bottom: 6px; white-space: nowrap; }
        .rx-appointment .rx-slots { letter-spacing: 1px; }
        .rx-signature { width: 55mm; margin: 0 auto; text-align: center; }
        /* Próxima cita y firma comparten fila, como en el recetario impreso */
        .rx-signature-row { display: flex; align-items: flex-end; justify-content: space-between; gap: 10px; }
        .rx-signature-row .rx-appointment { margin-bottom: 2px; }
        .rx-signature-row .rx-signature { margin: 0; }
        .rx-signature-line { border-top: 1px solid #1f2933; margin-bottom: 2px; }
        .rx-signature span { font-size: 8pt; color: #52606d; }
        .rx-contact { display: flex; gap: 14px; justify-content: flex-start; margin-top: 8px; font-size: 7.5pt; color: #52606d; }
        .rx-stamp { margin-top: 4px; font-size: 6.5pt; color: #9aa5b1; }

        /* Franja de color de la marca al pie de cada mitad */
        .rx-accent { height: 3px; background: ${pc}; border-radius: 2px; margin-top: 6px; }

        /* Margen fisico: casi ninguna impresora imprime hasta el borde, y sin
           este margen la columna derecha sale cortada en el papel. */
        @page { size: A4 landscape; margin: 5mm; }
        @media print {
          body { background: #fff; }
          .rx-sheet { width: auto; max-width: 100%; min-height: auto; margin: 0; padding: 3mm; box-shadow: none; }
          .no-print { display: none; }
        }
        @media screen {
          body { padding: 20px; }
          .rx-sheet { box-shadow: 0 2px 14px rgba(0,0,0,0.12); }
        }
      </style>
    </head>
    <body>
      <div class="rx-sheet">

        <!-- ================= MITAD IZQUIERDA: para farmacia ================= -->
        <div class="rx-half">
          ${header}

          <div class="rx-fields">
            ${field('Apellidos y Nombres:', patient.name)}
            <div class="rx-field-row">
              ${field('DNI:', patient.dni)}
              ${field('Edad:', patient.age !== null ? `${patient.age} años` : '')}
            </div>
            <div class="rx-field-row">
              ${field('Historia Clínica N°:', patient.historyNumber)}
              ${field('Fecha:', consultationDate)}
            </div>
          </div>

          ${diagnosisRows}

          <div class="rx-section-title">Tratamiento – Receta</div>
          ${table([
            { title: 'Medicamento<br>Dispositivo Médico (DCI)', width: '40%', value: med => med.nombre || '' },
            { title: 'Concentración', width: '20%', value: med => med.concentracion || '' },
            { title: 'Forma<br>Farmacéutica (F.F.)', width: '22%', value: med => med.forma_farmaceutica || '' },
            { title: 'Cantidad<br>(N° y letras)', width: '18%', value: med => med.cantidad || '' },
          ])}

          <div class="rx-bottom">
            ${signature}
            ${contactFooter}
            <div class="rx-accent"></div>
          </div>
        </div>

        ${options.show_cut_line ? '<div class="rx-cut"></div>' : ''}

        <!-- ================= MITAD DERECHA: para el paciente ================= -->
        <div class="rx-half">
          ${header}
          ${patientStrip}

          <div class="rx-section-title center">Indicaciones</div>
          ${table([
            // Nombre y concentración juntos: sin la dosis del fármaco el
            // paciente no puede verificar que le dieron lo correcto, y
            // "esomeprazol" a secas existe en 20 y en 40 mg.
            { title: 'Nombre del<br>medicamento', width: '25%',
              value: med => [med.nombre, med.concentracion].filter(Boolean).join(' ') },
            { title: 'Dosis', width: '13%', value: med => med.dosis || '' },
            { title: 'Frecuencia', width: '13%', value: med => med.frecuencia || '' },
            { title: 'Vía', width: '8%', value: med => med.via || '' },
            { title: 'Duración', width: '12%', value: med => med.duracion || '' },
            { title: 'Indicaciones', width: '29%', value: med => med.indicaciones || '' },
          ])}

          <div class="rx-notes">
            <span class="rx-notes-label">Indicaciones adicionales:</span>
            <div class="rx-notes-body">${additionalIndications()}</div>
          </div>

          <div class="rx-bottom">
            ${options.show_next_appointment ? `
            <div class="rx-signature-row">
              <div class="rx-appointment">
                Próxima cita: <span class="rx-slots">___ / ___ / ___</span>
              </div>
              ${signature}
            </div>` : signature}
            ${contactFooter}
            <div class="rx-accent"></div>
            ${options.show_digital_stamp ? `
            <p class="rx-stamp">Generado por ClinIA Perú - ${esc(new Date().toLocaleString('es-PE'))}</p>
            ` : ''}
          </div>
        </div>

      </div>
    </body>
    </html>
  `;
};

// ============================================================================
// API PÚBLICA
// ============================================================================

/** Plantillas disponibles, para poblar el selector de Configuración */
export const PRESCRIPTION_TEMPLATES = [
  {
    id: 'v1',
    name: 'Clásica vertical',
    description: 'Dos hojas A4 verticales: una de tratamiento y otra de receta para farmacia.',
  },
  {
    id: 'v2',
    name: 'Recetario horizontal',
    description: 'Una hoja A4 apaisada partida por línea de corte, con tablas de receta e indicaciones.',
  },
];

/**
 * Genera el HTML imprimible de la receta según la plantilla configurada.
 *
 * @param {Object} params
 * @param {Object} params.consultation - Consulta (diagnosis, indications, treatment_plan, consultation_date)
 * @param {Object} params.patient - Paciente (first_name, last_name, dni, birth_date)
 * @param {Object} params.branding - Branding y configuración de receta del médico
 * @param {string} [params.template] - Fuerza una plantilla; si se omite, usa la del médico
 * @returns {string} HTML completo y auto-contenido
 */
export const generatePrescriptionHTML = ({ consultation, patient, branding, template }) => {
  const plan = parseTreatmentPlan(consultation?.treatment_plan);
  const options = resolveOptions(branding);
  const doctor = buildDoctorInfo(branding);
  const patientInfo = buildPatientInfo(patient);
  const diagnoses = getDiagnoses(plan, consultation);
  const rowCount = resolveRowCount(branding, plan?.medicamentos?.length || 0);

  const ctx = {
    consultation: { ...consultation, diagnoses },
    plan,
    doctor,
    patient: patientInfo,
    options,
    rowCount,
  };

  const selected = template || branding?.prescription_template || 'v1';
  return selected === 'v2' ? renderTemplateV2(ctx) : renderTemplateV1(ctx);
};

/**
 * Abre el HTML en una ventana nueva, lista para imprimir o guardar como PDF.
 *
 * Con `autoPrint` se lanza el diálogo de impresión directamente; sin él se
 * muestra una barra superior con el botón, que es el flujo de "descargar PDF"
 * (el navegador guarda como PDF desde el mismo diálogo).
 *
 * @param {string} html - Documento a mostrar
 * @param {Object} [opts]
 * @param {boolean} [opts.autoPrint=false] - Lanzar impresión automáticamente
 * @param {string} [opts.accentColor='#2563eb'] - Color de la barra de instrucciones
 * @returns {boolean} false si el navegador bloqueó la ventana emergente
 */
export const openPrescriptionWindow = (html, { autoPrint = false, accentColor = '#2563eb' } = {}) => {
  const printWindow = window.open('', '_blank');
  if (!printWindow) return false;

  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.focus();

  if (autoPrint) {
    // El retardo da tiempo a que los logos en base64 se pinten antes de imprimir
    setTimeout(() => printWindow.print(), 250);
    return true;
  }

  const bar = printWindow.document.createElement('div');
  bar.className = 'no-print';
  bar.style.cssText = `position: fixed; top: 0; left: 0; right: 0; background: ${accentColor}; color: white; padding: 15px; text-align: center; z-index: 9999; font-family: 'Segoe UI', Arial, sans-serif;`;
  bar.innerHTML = `<strong>Para guardar como PDF:</strong> En el diálogo de impresión, seleccione "Guardar como PDF" como destino. <button onclick="window.print()" style="margin-left: 15px; padding: 8px 16px; background: white; color: ${accentColor}; border: none; border-radius: 4px; cursor: pointer; font-weight: bold;">Imprimir / Guardar PDF</button>`;
  printWindow.document.body.insertBefore(bar, printWindow.document.body.firstChild);
  return true;
};

/**
 * Datos de ejemplo para la vista previa de Configuración.
 * Permite al médico ver su plantilla sin abrir una consulta real.
 */
export const getPreviewSampleData = () => ({
  consultation: {
    consultation_date: new Date().toISOString(),
    diagnosis: 'Gastritis crónica (K29.5)',
    indications: 'Evitar alimentos irritantes, comidas ligeras cada 4 horas. Retornar si hay dolor persistente.',
    treatment_plan: JSON.stringify({
      diagnoses: [
        { diagnosis: 'Gastritis crónica', cie10_code: 'K29.5', is_primary: true },
        { diagnosis: 'Reflujo gastroesofágico', cie10_code: 'K21.0', is_primary: false },
      ],
      medicamentos: [
        {
          nombre: 'Omeprazol', concentracion: '20 mg', forma_farmaceutica: 'Cápsula',
          cantidad: '30 (treinta)', dosis: '1 cápsula', frecuencia: 'c/24h',
          via: 'Oral', duracion: '30 días',
          indicaciones: '30 min antes del desayuno',
        },
        {
          nombre: 'Sucralfato', concentracion: '1 g', forma_farmaceutica: 'Suspensión',
          cantidad: '1 frasco', dosis: '10 mL', frecuencia: 'c/8h',
          via: 'Oral', duracion: '14 días',
          indicaciones: 'Una hora después de los alimentos',
        },
      ],
      laboratorio: ['Hemograma completo', 'Test de ureasa'],
      imagenes: [],
      interconsultas: [],
      procedimientos: [],
      nutricion: 'Dieta blanda, fraccionada en 5 comidas al día.',
    }),
  },
  patient: {
    first_name: 'Juan',
    last_name: 'Pérez García',
    dni: '12345678',
    birth_date: '1990-05-14',
    medical_record_number: 'HC-004821',
  },
});
